import { useEffect, useMemo, useState, useLayoutEffect, MutableRefObject } from "react";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  applyQuickFilter,
  clearQuickFilter,
  setQuickFilterOrder,
} from "src/redux/actions/flights/FlightTimeline.Actions";
// Material UI
import { useTheme } from "@mui/material/styles";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import Tab from "@mui/material/Tab";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Tooltip,
  styled,
  Box,
  Button,
  Collapse,
  alpha,
  IconButton,
  Checkbox,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SortIcon from "@mui/icons-material/Sort";
import { aircraftFilterLabel, convertSelectedFiltersToFilterOptions, sameAircraftSet } from "./TimelineSortFilter";

// Styling
import classNames from "classnames";

// Menu Tab Content
import { AircraftSortFilter } from "./TimelineSortFilter";
import { TimelineFlightHighlight } from "./TimelineFlightHighlight";
import { TimelineAddEvents } from "./TimelineAddEvents";
import { TimelineViewScale } from "./TimelineViewScale";
import { TimelineControls } from "./TimelineControls";
import { TimelineFocus } from "./TimelineFocus";
import { TimelineActions } from "./TimelineActions";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { IFlight, IGroundEvent, IUserAppSettings } from "@vmosolutions/oc-interfaces";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

import { IAircraftFilterOption, defaultAircraftQuickFilter, isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { mimicShiftClickAndScroll } from "src/pages/CrewSchedule/CrewScheduleHeader/CrewScheduleHeader";

enum Menu {
  SORT = "Aircraft Filter",
  HIGHLIGHT = "Flight Highlight",
  BUILD = "Add Timeline Events",
  VIEW = "View Scale",
  CONTROLS = "Timeline Controls",
  OPTIONS = "Options",
  FOCUS = "Focus Date/Selections",
  ACTIONS = "Actions",
}
export interface IDimensions {
  width: number;
  height: number;
}

interface IFlightTimelineHeaderProps {
  id: string;
  path: string[];
  timelineContainer: MutableRefObject<HTMLDivElement>;
  selectedEvents: (IFlight | IGroundEvent)[];
  setFocusOnEvent: (event: IFlight | IGroundEvent) => void;
  setFocusOnDate: (jumpDate: Date) => void;
  setViewportScale: (scale?: string | number[]) => void;
  viewScale: string;
  startDate: Date;
  endDate: Date;
  setActiveEventModal: (activeEventModal: EventModalTypes) => void;
  resizeViewport: () => void;
  timelineLocked: boolean;
  setTimelineLocked: () => void;
  isDisplayUTC: boolean;
  setDisplayUTC: (value: boolean) => void;
  isDisplayICAO: boolean;
  setDisplayICAO: (value: boolean) => void;
  highlightIsActive: boolean;
  highlightConditions?: string;
  showMaintenanceCounters: boolean;
  setShowMaintenanceCounters: (value: boolean) => void;
  /** The selected aircraft that are on screen, in display order - what the quick filter would pin to (ENG-715). */
  selectedAircraftIds: string[];
  /** Every aircraft currently on screen, i.e. what "select all" would select. */
  displayedAircraftIds: string[];
  /** Toolbar select all / clear selection control. */
  onSelectAllAircraft: () => void;
}

const OCFlightTimelineHeader = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const OCFlightTimelineHeaderContent = styled(Grid)(({ theme }) => ({
  "& .card": {
    backgroundColor: theme.custom.timeline.contextListBackground,
    boxShadow: theme.shadows[5],
    margin: "0px 8px 8px 8px",
    "& .MuiCardContent-root": {
      padding: 0,
      "& button": {
        margin: 5,
        padding: "2px 5px",
        fontSize: 12,
        fontWeight: 300,
        "&.find_previous, &.find_next": {
          padding: 0,
        },
      },
      "& h6.body_text": {
        display: "inline-flex",
        margin: 5,
        padding: 2,
        height: 24,
        lineHeight: "20px",
      },
    },
  },
}));

const OCFlightTimelineHeaderMenu = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  // This row does not wrap, so once it runs out of width - two flight displays side by side in a mosaic - the browser
  // shrinks every control in it proportionally and each one clips its own label ("Show 2 only" losing its last word
  // into DRAG MODE, "LOCAL" cut to "LOC"). The tab strip is scrollable and built to give up space, the controls are
  // not, so weight the shrinking heavily towards the tabs: they yield first and the controls keep their labels. The
  // controls still shrink as a last resort, so a window too narrow even for that degrades the way it used to rather
  // than overflowing into its neighbour.
  overflow: "hidden",
  "& > *": { flexShrink: 1 },
  "& > .MuiTabs-root": { flexShrink: 1000, minWidth: 0 },
  // Aircraft quick filter (ENG-715) - sits at the far left of the toolbar, above the aircraft column.
  // Select all / clear lives here rather than in the column header so it costs the column no horizontal width.
  "& .aircraft_select_all_wrapper": {
    display: "flex",
    alignItems: "center",
    height: 34,
    paddingLeft: "6px",
    "& .aircraft_select_all_checkbox": {
      padding: 0,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 18 },
      "&:hover": { color: theme.palette.primary.main, backgroundColor: "transparent" },
      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: theme.palette.primary.main },
      "&.Mui-disabled": { opacity: 0.3 },
    },
  },
  "& .aircraft_quick_filter_wrapper": {
    display: "flex",
    alignItems: "center",
    height: 34,
    padding: "0px 4px 0px 4px",
    // "Show N only" and "Reset" can be shown side by side while an applied filter is being narrowed.
    gap: 4,
    // Restores the sorted order after rows were dragged around in free mode.
    "& .aircraft_quick_filter_restore_btn": {
      padding: 3,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:hover": { color: theme.palette.primary.light, backgroundColor: alpha(theme.palette.primary.main, 0.12) },
    },
    "& .aircraft_quick_filter_btn": {
      height: 26,
      minWidth: 0,
      padding: "0px 8px",
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "26px",
      whiteSpace: "nowrap",
      textTransform: "none",
      borderColor: alpha(theme.palette.primary.main, 0.5),
      "& .MuiButton-startIcon": { marginRight: 4, marginLeft: -2 },
      "& .MuiSvgIcon-root": { fontSize: 16 },
      // Applied state: a quiet "toggle is on" look rather than an alert - soft tint, solid border, no fill.
      "&.active": {
        color: theme.palette.primary.light,
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.16),
        "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.26) },
      },
    },
  },
  "& .hovered_flight_preview_wrapper": {
    display: "flex",
    flexShrink: 0,
    borderRadius: 5,
    width: 128,
    height: 30,
    margin: "0px 8px",
    border: "1px dotted " + theme.custom.forms.labels,
    "& .flightChip_container": {
      justifyContent: "center",
      alignContent: "center",
      "& .placeholder_text": {
        textTransform: "uppercase",
        color: theme.custom.forms.labels,
        fontWeight: 300,
        fontSize: 12,
      },
      "& .flightchip": { margin: 3 },
    },
  },

  "& .timeline_lock_switch_wrapper": {
    display: "flex",
    "& .drag_mode_switch": {
      width: 170,
      height: 30,
      margin: 2,
      "&.focused label": { color: "unset" },
      "& label": {
        margin: 0,
        width: 40,
        whiteSpace: "unset",
      },
      "& .switch-buttons": {
        marginLeft: 8,
        "& button": { padding: "0px 8px" },
      },
    },
  },

  "& .timeline_label_toggle": {
    display: "flex",
    height: 30,
    margin: 2,
    padding: 2,
    "& .toggles_label": {
      margin: "2px 10px 0px 0px",
      width: 40,
      fontFamily: "Roboto, Helvetica, arial, sans-serif",
      fontWeight: 400,
      fontSize: 12,
      textAlign: "right",
      lineHeight: "12px",
    },
    "&.maintenance_toggle": {
      flexDirection: "column",
      alignItems: "center",
      marginLeft: 4,
      height: "auto",
      "& .toggles_label": {
        margin: 0,
        width: "auto",
        textAlign: "center",
      },
      "& .toggle_label": {
        "& .switch-buttons button.disabled": {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.background.default,
        },
      },
    },
    "& .toggle_label": {
      margin: 0,
      width: "auto",
      "&.focused label": { color: "unset" },
      "& label": {
        margin: 0,
        width: 40,
      },
      "& .switch-buttons": {
        margin: "-4px 4px -4px 0px",
        "& button": { height: 12 },
      },
      "&.active": {
        "& .switch-buttons button.disabled": {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.background.default,
        },
      },
      "&.display_utc": {
        marginBottom: "1px",
      },
    },
  },
  // Add text overflow ellipsis to the aircraft sort tab.
  "& .MuiTabs-root button.tab-SORT.MuiTab-wrapped": {
    display: "inline-block",
    maxWidth: 175,
    maxHeight: 24,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

/**
 * Component created to dynamically render content based on selected tabs in the timeline header menu.
 * @param props -
 * @param state -
 */
export const FlightTimelineHeader = (props: IFlightTimelineHeaderProps): JSX.Element => {
  const {
    id,
    resizeViewport,
    timelineLocked,
    setTimelineLocked,
    isDisplayICAO,
    isDisplayUTC,
    setDisplayICAO,
    setDisplayUTC,
    highlightIsActive,
    highlightConditions,
    showMaintenanceCounters,
    setShowMaintenanceCounters,
    selectedAircraftIds,
    displayedAircraftIds,
    onSelectAllAircraft,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState<boolean | string>(false);

  // Aircraft quick filter (ENG-715), held per flight display window; `id` is this window's mosaic id.
  // "Show N only" pins the filter to the aircraft selected right now, so it appears whenever the selection differs
  // from what is on screen: before filtering, and again once the user narrows an applied filter by deselecting rows.
  // Resetting keeps the selection so the user can correct it and re-apply.
  const quickFilter =
    useSelector((state: IRootState) => state.timelineFlights.quickFilters?.[id]) ?? defaultAircraftQuickFilter;
  const quickFilterActive = (quickFilter.aircraft?.length ?? 0) > 0;
  // Rows have been dragged into a custom order while the quick filter was applied ("free mode").
  const quickFilterHasCustomOrder = (quickFilter.order?.length ?? 0) > 0;
  const selectedAircraftCount = selectedAircraftIds?.length ?? 0;
  const visibleAircraftCount = displayedAircraftIds?.length ?? 0;
  // Nothing to apply when the selection already matches the rows on screen - filtering to them would change nothing.
  const canApplyQuickFilter = selectedAircraftCount > 0 && !sameAircraftSet(selectedAircraftIds, displayedAircraftIds);
  const allAircraftSelected = selectedAircraftCount > 0 && selectedAircraftCount === visibleAircraftCount;

  // Context menu management
  const flightContextMenus = useSelector((state: IRootState) => state.timelineFlights.isFlightContextOpen);
  const isContextMenuOpen = flightContextMenus && id ? flightContextMenus[id] : false;
  const menuElement = document.getElementById(`${id}-contextmenu`);
  const [contextWidth, setContextWidth] = useState<number>(0);
  // Added so that if empty, switch for drag mode is disabled.
  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);

  // Aircraft Filtering.
  const filters = useSelector((state: IRootState) => state.timelineFlights.aircraftFilters);

  const aircraftGroups = useSelector((state: IRootState) => state.structures.customerStructure.aircraftGroups);
  const allAircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);

  const userAppSettings: IUserAppSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  const { blockInfo } = userAppSettings;

  const isMaintenanceEnabled = useMemo(() => {
    return allAircraft?.some((ac) => ac.maintenance != null) ?? false;
  }, [allAircraft]);

  //if the aircraft groups change - update the timeline filter
  const aircraftFilters: IAircraftFilterOption[] = useMemo(
    () => convertSelectedFiltersToFilterOptions(filters, allAircraft, aircraftGroups),
    [filters, allAircraft, aircraftGroups]
  );

  const filteredAircraftLabel = useMemo(() => aircraftFilterLabel(aircraftFilters), [aircraftFilters]);

  // If tabValues change, resize the timeline viewport accordingly.
  // The resize function is wrapped in a setTimeout to ensure tab component is visible before resize occurs.
  useLayoutEffect(() => resizeViewport && resizeViewport(), [resizeViewport, tabValue]);

  useEffect(() => {
    if (isDisplayICAO !== blockInfo.icao) setDisplayICAO(blockInfo.icao);
    if (isDisplayUTC !== blockInfo.utc) setDisplayUTC(blockInfo.utc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockInfo.icao, blockInfo.utc]);

  // Use effect fired off if user opens the context menu.
  // Returns the actual width of the menu after a slight delay.
  useEffect(() => {
    const menuWidthTimer = setTimeout(() => {
      const menuWidth =
        isContextMenuOpen && menuElement?.firstElementChild ? menuElement.firstElementChild.clientWidth : 0;
      setContextWidth(menuWidth);
    }, 100);
    return () => clearTimeout(menuWidthTimer);
  }, [isContextMenuOpen, menuElement, id]);

  /**
   * Wrapper function for the menu tabs. Place any repeating code here.
   * Timeline functions are passed in as props and applied to the components with spread operators.
   * @param index -
   */
  const tabContent = (): JSX.Element => {
    if (!!tabValue && typeof tabValue === "string") {
      const {
        id,
        path,
        timelineContainer,
        setFocusOnEvent,
        setFocusOnDate,
        selectedEvents,
        setViewportScale,
        viewScale,
        startDate,
        endDate,
        setActiveEventModal,
      } = props;

      switch (tabValue) {
        case "SORT":
          return (
            <AircraftSortFilter
              timelineContainer={timelineContainer}
              aircraftFilters={aircraftFilters}
              filteredAircraftLabel={filteredAircraftLabel}
              setTabValue={setTabValue}
            />
          );
        case "HIGHLIGHT":
          return <TimelineFlightHighlight setTabValue={setTabValue} />;
        case "BUILD":
          return <TimelineAddEvents id={id} path={path} setFocusOnEvent={setFocusOnEvent} setTabValue={setTabValue} />;
        case "VIEW":
          return (
            <TimelineViewScale setViewportScale={setViewportScale} viewScale={viewScale} setTabValue={setTabValue} />
          );
        case "CONTROLS":
          return <TimelineControls setTabValue={setTabValue} />;
        case "FOCUS":
          return (
            <TimelineFocus
              selectedEvents={selectedEvents}
              setFocusOnEvent={setFocusOnEvent}
              setFocusOnDate={setFocusOnDate}
              setViewportScale={setViewportScale}
              startDate={startDate}
              endDate={endDate}
              setTabValue={setTabValue}
            />
          );
        case "ACTIONS":
          return <TimelineActions setActiveEventModal={setActiveEventModal} setTabValue={setTabValue} />;
        default:
          return null;
      }
    }
    return null;
  };

  const handleSelectTab = (event: any, newValue: any): void => {
    const value = newValue === tabValue ? false : newValue;
    setTabValue(value);
  };

  const draggableEvents = useMemo(() => {
    return selectedEvents.filter(
      (event: IFlight | IGroundEvent) =>
        (!isFlight(event) &&
          (event.eventType?.isGroundTurnBack || event.eventType?.isCrewable || event.eventType?.isSim)) ||
        isFlight(event)
    );
  }, [selectedEvents]);

  return (
    <OCFlightTimelineHeader container alignContent="flex-start">
      <OCFlightTimelineHeaderMenu item style={{ width: `calc(100% - ${contextWidth}px)` }}>
        <div className="aircraft_select_all_wrapper">
          <Tooltip
            title={
              selectedAircraftCount > 0
                ? `Clear the aircraft selection (${selectedAircraftCount} selected)`
                : "Select all aircraft. Click an aircraft in the column to select it on its own."
            }
            enterDelay={500}
            placement="bottom"
          >
            <span>
              <Checkbox
                id="aircraft_select_all_checkbox"
                className="aircraft_select_all_checkbox"
                size="small"
                disableRipple
                disabled={!visibleAircraftCount}
                checked={allAircraftSelected}
                indeterminate={selectedAircraftCount > 0 && !allAircraftSelected}
                onClick={onSelectAllAircraft}
                inputProps={{ "aria-label": "Select all aircraft or clear the selection" }}
              />
            </span>
          </Tooltip>
        </div>
        <Collapse in={canApplyQuickFilter || quickFilterActive} orientation="horizontal" unmountOnExit>
          <div className="aircraft_quick_filter_wrapper">
            {canApplyQuickFilter && (
              <Tooltip
                title={`Only show the ${selectedAircraftCount} selected aircraft`}
                enterDelay={500}
                placement="bottom"
              >
                <Button
                  id="aircraft_quick_filter_btn"
                  className="aircraft_quick_filter_btn"
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterAltIcon />}
                  onClick={() => dispatch(applyQuickFilter(selectedAircraftIds, id))}
                >
                  {`Show ${selectedAircraftCount} only`}
                </Button>
              </Tooltip>
            )}
            {quickFilterActive && (
              <Tooltip title="Show all aircraft again (the selection is kept)" enterDelay={500} placement="bottom">
                <Button
                  id="aircraft_quick_filter_reset_btn"
                  className={classNames("aircraft_quick_filter_btn", "active")}
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterAltOffIcon />}
                  onClick={() => dispatch(clearQuickFilter(id))}
                >
                  Reset
                </Button>
              </Tooltip>
            )}
            {quickFilterActive && quickFilterHasCustomOrder && (
              <Tooltip
                title="Aircraft have been dragged into a custom order. Click to restore the sorted order."
                enterDelay={500}
                placement="bottom"
              >
                <IconButton
                  id="aircraft_quick_filter_restore_btn"
                  className="aircraft_quick_filter_restore_btn"
                  size="small"
                  onClick={() => dispatch(setQuickFilterOrder(null, id))}
                  aria-label="Restore sorted aircraft order"
                >
                  <SortIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Collapse>
        <div className={classNames("timeline_lock_switch_wrapper")}>
          <SwitchField
            className="drag_mode_switch"
            name="dragMode"
            value={!timelineLocked}
            onChange={setTimelineLocked}
            switchlabels={["Inactive", "Active"]}
            switchtooltips={["Unlock Timeline Movement", "Lock Timeline Movement"]}
            label="DRAG MODE"
            disabled={draggableEvents.length === 0}
            allowNull={false}
          />
        </div>

        <div className={classNames("timeline_label_toggle")}>
          <Typography className="toggles_label">BLOCK INFO</Typography>
          <div className="switch_fields">
            <SwitchField
              className={`display_utc toggle_label ${!isDisplayUTC && "active"}`}
              name="UTC_Local"
              value={isDisplayUTC}
              onChange={setDisplayUTC}
              switchlabels={["UTC", "LOCAL"]}
              switchtooltips={["Times as UTC", "Times as local"]}
              label=""
              allowNull={false}
            />
            <SwitchField
              className={`display_icao toggle_label ${!isDisplayICAO && "active"}`}
              name="IATA_ICAO"
              value={isDisplayICAO}
              onChange={setDisplayICAO}
              switchlabels={["ICAO", "IATA"]}
              switchtooltips={["Locations as ICAO", "Locations as IATA"]}
              label=""
              allowNull={false}
            />
          </div>
          <Box sx={{ ml: 1, display: "flex", justifyContent: "end", alignItems: "center" }}>
            <Tooltip title="Zoom Schedule Out">
              <ZoomOutIcon
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  mimicShiftClickAndScroll(".timeline_webgl_container", 200);
                }}
              />
            </Tooltip>
            <Tooltip title="Zoom Schedule In">
              <ZoomInIcon
                sx={{ cursor: "pointer" }}
                onClick={() => mimicShiftClickAndScroll(".timeline_webgl_container", -200)}
              />
            </Tooltip>
          </Box>
        </div>

        <div
          className={classNames("timeline_label_toggle", "maintenance_toggle")}
          style={{ display: isMaintenanceEnabled ? "inherit" : "none" }}
        >
          <Typography className="toggles_label">MAINTENANCE</Typography>
          <SwitchField
            className={`toggle_label ${showMaintenanceCounters ? "active" : ""}`}
            name="maintenanceCounters"
            value={showMaintenanceCounters}
            onChange={setShowMaintenanceCounters}
            switchlabels={["ON", "OFF"]}
            switchtooltips={["Show Maintenance Counters", "Hide Maintenance Counters"]}
            label=""
            allowNull={false}
            disabled={!isMaintenanceEnabled}
          />
        </div>

        <OcTabs variant="scrollable" scrollButtons="auto" value={tabValue} onChange={handleSelectTab}>
          <Tab
            wrapped={true}
            value="SORT"
            label={
              quickFilterActive ? (
                <Tooltip
                  enterDelay={1000}
                  placement="top"
                  title={`Quick filter active: showing ${visibleAircraftCount} of the aircraft (${Menu.SORT}: ${filteredAircraftLabel})`}
                >
                  <div>{`${Menu.SORT}: ${visibleAircraftCount} aircraft`}</div>
                </Tooltip>
              ) : (
                `${Menu.SORT}: ${filteredAircraftLabel}`
              )
            }
          />
          <Tab
            wrapped={true}
            value="HIGHLIGHT"
            label={
              <Tooltip enterDelay={1000} placement="top" title={highlightConditions ?? ""}>
                <div>{`${Menu.HIGHLIGHT} ${highlightIsActive ? ": Active" : ""}`}</div>
              </Tooltip>
            }
          />
          <Tab wrapped={true} value="BUILD" label={Menu.BUILD} />
          <Tab wrapped={true} value="VIEW" label={`${Menu.VIEW}: ${props.viewScale}`} />
          <Tab wrapped={true} value="CONTROLS" label={Menu.CONTROLS} />
          <Tab wrapped={true} value="FOCUS" label={Menu.FOCUS} />
          <Tab wrapped={true} value="ACTIONS" label={Menu.ACTIONS} />
        </OcTabs>
      </OCFlightTimelineHeaderMenu>
      <OCFlightTimelineHeaderContent item style={{ width: `calc(100% - ${contextWidth}px)` }}>
        <Card classes={{ root: classNames("card", tabValue) }}>
          <CardContent>{tabContent()}</CardContent>
        </Card>
      </OCFlightTimelineHeaderContent>
    </OCFlightTimelineHeader>
  );
};
